import { Button } from "@/components/Button";
import { KLAVIYO_CREATE_API_KEY_URL } from "@/utils/const";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import KlaviyoApiKeyForm from "@/components/Form/KlaviyoApiKeyForm.tsx";
import { useTracking } from "@/requests/hooks/app.ts";
import { useGetKlaviyoApiKey } from "@/requests/hooks/collect.ts";
import Shimmer from "@/components/Shimmer";
import Accordion from "@/components/Accordion";
import OnboardingVideo from "@/components/OnboardingVideo";
import createKlaviyoPrivateApiKey from "@/assets/videos/collect/onboarding/create-a-private-api-key-in-klaviyo.mp4";
import { Bold } from "@/components/Typography";

const KlaviyoApiKey = () => {
  const { t } = useTranslation();
  const { mutate: sendTracking } = useTracking();

  const { isPending: isGetKlaviyoApiKeyPending, data: getKlaviyoApiKeyData } =
    useGetKlaviyoApiKey();

  useEffect(() => {
    sendTracking("control-panel.collect.integration-settings.klaviyo-key.view");
  }, []);

  const renderInputAndButton = () => {
    if (isGetKlaviyoApiKeyPending)
      return (
        <>
          <div className="my-10">
            <Shimmer height="35px" width="250px" />
          </div>
          <div className="my-10">
            <Shimmer height="35px" width="730px" />
          </div>
          <div className="my-10">
            <Shimmer height="65px" width="100%" />
          </div>
          <div className="my-35">
            <Shimmer height="70px" width="260px" />
          </div>
        </>
      );

    return (
      <KlaviyoApiKeyForm
        klaviyoApiKey={getKlaviyoApiKeyData?.privateApiKey}
        isKlaviyoApiKeyPending={isGetKlaviyoApiKeyPending}
      />
    );
  };

  const renderAccordionBody = () => (
    <div className="grid grid-cols-2 items-center gap-50">
      <ol className="list-decimal ps-20">
        <li>
          <Trans i18nKey="settings.integration.klaviyoKey.instructions.step1">
            Zero
            <Button
              as="a"
              variant="link"
              href={KLAVIYO_CREATE_API_KEY_URL}
              target="_blank"
              trackingId="onboarding.collect.klaviyo.go-to-private-key"
              width="unset"
              icon={faCaretRight}
              iconClassName="h-15"
              iconPosition="end">
              one
            </Button>
            Two
          </Trans>
        </li>
        <li>
          <Trans i18nKey="settings.integration.klaviyoKey.instructions.step2">
            Zero
            <Bold>One</Bold>
          </Trans>
        </li>
        <li>
          <Trans i18nKey="settings.integration.klaviyoKey.instructions.step3">
            one
            <Bold>two</Bold>
          </Trans>
        </li>
        <li>
          <Trans i18nKey="settings.integration.klaviyoKey.instructions.step4">
            Zero
            <Bold>One</Bold>
          </Trans>
        </li>

        <li>
          <Trans i18nKey="settings.integration.klaviyoKey.instructions.step5">
            Zero
            <Bold>One</Bold>
            two
          </Trans>
        </li>
        <li>
          <Trans i18nKey="settings.integration.klaviyoKey.instructions.step6">
            Zero
            <Bold>One</Bold>
          </Trans>
        </li>
      </ol>
      <OnboardingVideo videoLocation={createKlaviyoPrivateApiKey} />
    </div>
  );

  const renderAccordion = () => (
    <Accordion
      defaultIsExpanded
      topContent={
        <Bold>{t("settings.integration.klaviyoKey.instructions.title")}</Bold>
      }>
      {renderAccordionBody()}
    </Accordion>
  );

  return (
    <div className="mt-50">
      {renderInputAndButton()}
      {renderAccordion()}
    </div>
  );
};

export default KlaviyoApiKey;
